import { useNavigate } from "@tanstack/react-router"
import { Alert, AlertDescription, AlertTitle } from "design-system/atoms/Alert"
import { useTrackEvent } from "lib/mixpanel"
import { PLAN_DEFAULT_TRANSLATIONS, PLANS, TRACKING_EVENTS, type Plan } from "lopermut"
import { Loading } from "modules/shared/components/Loading"
import { RouteError } from "modules/shared/components/NotFound"
import { PricingCard } from "modules/subscription/components/Plans/PricingCard"
import { useGetProducts } from "modules/subscription/hooks/useGetProducts"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CheckoutDialog } from "./CheckoutDialog"
import { PricingTab } from "./PricingTab"

const CARD_FEATURE_KEYS = ["channels", "topUpPricePerHundred", "credits"] as const

const frequencies = [
  {
    key: "month",
    discount: 0,
  } as const,
  {
    key: "year",
    discount: 20,
  } as const,
]

type Props = {
  checkout?: "success" | "cancel"
}

export function Plans({ checkout }: Props) {
  const { t } = useTranslation()
  const trackEvent = useTrackEvent()
  const navigate = useNavigate()

  const { data, isLoading, isError, error } = useGetProducts()
  const [frequency, setFrequency] = useState<"month" | "year">("year")

  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(Boolean(checkout))

  const resolveFeature = useCallback(
    (featureKey: keyof Plan["features"], plan: Plan): string => {
      const translationKey =
        featureKey === "channels"
          ? `subscription.plans.features.channels_${(plan.features.channels?.length ?? 0) > 1 ? "all" : "inbound"}`
          : `subscription.plans.features.${featureKey}`
      const defaultFn = PLAN_DEFAULT_TRANSLATIONS.features[featureKey]
      return t(translationKey, {
        credits: plan.features.credits.toLocaleString("de-DE"),
        topUpPrice: plan.features.topUpPricePerHundred,
        defaultValue: defaultFn ? defaultFn(plan) : "",
      })
    },
    [t],
  )

  const plans = useMemo(() => {
    if (!data) return []

    return PLANS.filter((p) => !p.isHidden)
      .map((p) => {
        const product = data.find((d) => {
          const name = d.name.split("_").at(-1)
          return name === p.key
        })

        if (!product) return null

        const defKey = `subscription.plans.definitions.${p.key}`

        return {
          key: p.key,
          ...product,
          name: t(`${defKey}.name`, { defaultValue: PLAN_DEFAULT_TRANSLATIONS.plans[p.key].name }),
          description: t(`${defKey}.description`, {
            defaultValue: PLAN_DEFAULT_TRANSLATIONS.plans[p.key].description,
          }),
          features: CARD_FEATURE_KEYS.map((fk) => resolveFeature(fk, p)),
          isPopular: p.isPopular,
          isContactUs: p.isContactUs,
        }
      })
      .filter(Boolean)
  }, [data, t, resolveFeature])

  useEffect(() => {
    if (checkout && checkout === "success" && data) {
      const activePlan = data.find((p) => p.isActive)

      trackEvent(TRACKING_EVENTS.CHECKOUT_COMPLETED, {
        planName: activePlan?.name,
      })
    }
  }, [checkout, data, trackEvent])

  if (isLoading) return <Loading />

  if (isError) return <RouteError showPage={false} error={error} />

  const activeIndex = plans.findIndex((plan) => plan.isActive)
  const hasActivePlan = activeIndex !== -1

  return (
    <>
      <div className="flex flex-col items-center text-center gap-8 p-5">
        <div className="flex flex-col gap-4">
          <h1 className="text-4xl font-medium md:text-5xl">{t("subscription.plans.title")}</h1>
          <p>{t("subscription.plans.description")}</p>
          {!hasActivePlan && (
            <Alert variant="gradient" className="my-2 p-4">
              <AlertTitle className="text-foreground text-lg">
                {t("subscription.usage.joinAPlan.title")}
              </AlertTitle>
              <div className="flex flex-col gap-0.5">
                <AlertDescription className="text-foreground text-base">
                  {t("subscription.usage.joinAPlan.note")}
                </AlertDescription>
                <AlertDescription>{t("subscription.usage.joinAPlan.description")}</AlertDescription>
              </div>
            </Alert>
          )}
          <div className="mx-auto flex w-fit rounded-full bg-muted p-1">
            {frequencies.map((freq) => (
              <PricingTab
                key={freq.key}
                id={freq.key}
                text={t(`subscription.plans.tabs.${freq.key}`)}
                selected={frequency === freq.key}
                setSelected={(text) => setFrequency(text as (typeof frequencies)[0]["key"])}
                discount={
                  freq.discount > 0
                    ? t("subscription.plans.discount", { discount: freq.discount })
                    : undefined
                }
              />
            ))}
          </div>
        </div>
        <div className="flex flex-wrap justify-center gap-6 min-[1800px]:pt-8 w-full max-w-[1000px] min-[1800px]:max-w-none">
          {plans.map((plan, index) => (
            <PricingCard
              key={plan.key}
              frequency={frequency}
              isPopular={plan.isPopular ?? false}
              isActive={plan.isActive}
              name={plan.name}
              description={plan.description}
              features={plan.features}
              prices={plan.prices}
              canUpgrade={activeIndex < index}
              isContactUs={plan.isContactUs ?? false}
            />
          ))}
        </div>
        <p className="text-sm text-muted-foreground">{t("subscription.plans.taxNote")}</p>
      </div>
      {checkoutDialogOpen && (
        <CheckoutDialog
          kind={checkout ?? "cancel"}
          onClose={() => {
            setCheckoutDialogOpen(false)
            navigate({ to: "/settings/subscription/plans", search: {} })
          }}
        />
      )}
    </>
  )
}
